(() => {
  'use strict';

  const API_BASE = 'https://upload.lausitzwerbung.de/api';
  const SHOPIFY_DIRECT_LIMIT = 20 * 1024 * 1024;
  const ALLINKL_MAX_LIMIT = 350 * 1024 * 1024;
  const RETRY_DELAYS = [1200, 2500, 5000];

  const SHOPIFY_DIRECT_EXTENSIONS = new Set([
    'pdf', 'png', 'jpg', 'jpeg', 'svg',
  ]);

  const ALL_ALLOWED_EXTENSIONS = new Set([
    'pdf', 'png', 'jpg', 'jpeg', 'svg', 'tif', 'tiff', 'psd',
    'heic', 'heif', 'ai', 'eps',
  ]);

  /** @type {Set<HTMLInputElement>} */
  const initializedInputs = new Set();

  let activeUploads = 0;

  window.addEventListener('beforeunload', (event) => {
    if (activeUploads <= 0) return;
    event.preventDefault();
    event.returnValue = '';
  });

  function formatBytes(bytes) {
    if (!Number.isFinite(bytes) || bytes <= 0) return '0 MB';
    const mb = bytes / (1024 * 1024);
    if (mb >= 100) return `${mb.toFixed(0)} MB`;
    if (mb >= 10) return `${mb.toFixed(1)} MB`;
    return `${mb.toFixed(2)} MB`;
  }

  function formatSpeed(bytesPerSecond) {
    if (!Number.isFinite(bytesPerSecond) || bytesPerSecond <= 0) return '–';
    return `${formatBytes(bytesPerSecond)}/s`;
  }

  function formatRemaining(seconds) {
    if (!Number.isFinite(seconds) || seconds < 0) return 'wird berechnet';
    if (seconds < 10) return 'weniger als 10 Sekunden';
    if (seconds < 60) return `ca. ${Math.ceil(seconds)} Sekunden`;
    const minutes = Math.ceil(seconds / 60);
    return `ca. ${minutes} Minute${minutes === 1 ? '' : 'n'}`;
  }

  function getExtension(filename) {
    const dot = filename.lastIndexOf('.');
    return dot === -1 ? '' : filename.slice(dot + 1).toLowerCase();
  }

  async function fetchJson(url, options = {}) {
    let response;
    try {
      response = await fetch(url, options);
    } catch (error) {
      const networkError = new Error('Netzwerkverbindung zum Uploadserver fehlgeschlagen.');
      networkError.cause = error;
      networkError.code = 'network_error';
      throw networkError;
    }

    const text = await response.text();
    let payload = null;

    if (text.trim() !== '') {
      try {
        payload = JSON.parse(text);
      } catch {
        const parseError = new Error('Der Uploadserver hat eine unerwartete Antwort geliefert.');
        parseError.code = 'invalid_server_response';
        parseError.httpStatus = response.status;
        throw parseError;
      }
    }

    if (!response.ok || payload?.ok === false) {
      const message = payload?.message || payload?.error || `Uploadserver-Fehler (${response.status})`;
      const serverError = new Error(message);
      serverError.code = payload?.error || 'server_error';
      serverError.httpStatus = response.status;
      serverError.payload = payload;
      throw serverError;
    }

    return payload || {};
  }

  function storageKey(input) {
    const productId = input.closest('[data-product-id]')?.getAttribute('data-product-id') || '';
    const formId = input.getAttribute('form') || input.form?.id || 'product';
    return `lw-large-upload-v1:${productId}:${formId}`;
  }

  function loadSession(input) {
    try {
      const raw = localStorage.getItem(storageKey(input));
      if (!raw) return null;
      const session = JSON.parse(raw);
      if (!session || typeof session !== 'object') return null;
      return session;
    } catch {
      return null;
    }
  }

  function saveSession(input, session) {
    try {
      localStorage.setItem(storageKey(input), JSON.stringify(session));
    } catch {
      // Der Upload funktioniert auch ohne LocalStorage; nur Fortsetzen faellt dann weg.
    }
  }

  function clearSession(input) {
    try {
      localStorage.removeItem(storageKey(input));
    } catch {
      // no-op
    }
  }

  function getUi(input) {
    const wrapper = input.closest('[data-lw-upload-wrapper]') || input.closest('.lw-pilot-upload');
    return {
      wrapper,
      status: wrapper?.querySelector('[data-lw-upload-status]') || null,
      title: wrapper?.querySelector('[data-lw-upload-status-title]') || null,
      detail: wrapper?.querySelector('[data-lw-upload-status-detail]') || null,
      progress: wrapper?.querySelector('[data-lw-upload-progress]') || null,
      progressValue: wrapper?.querySelector('[data-lw-upload-progress-value]') || null,
      progressText: wrapper?.querySelector('[data-lw-upload-progress-text]') || null,
      speed: wrapper?.querySelector('[data-lw-upload-speed]') || null,
      remaining: wrapper?.querySelector('[data-lw-upload-remaining]') || null,
      cancel: wrapper?.querySelector('[data-lw-upload-cancel]') || null,
    };
  }

  function setBoxState(input, state, message = '') {
    const box = input.closest('[data-lw-personalization]');
    if (!box) return;
    box.dataset.lwLargeUploadState = state;
    box.dataset.lwLargeUploadMessage = message;
  }

  function setStatus(input, state, options = {}) {
    const ui = getUi(input);
    if (!ui.status) return;

    ui.status.hidden = false;
    ui.status.dataset.state = state;

    if (ui.title) ui.title.textContent = options.title || '';
    if (ui.detail) ui.detail.textContent = options.detail || '';

    if (ui.progress) {
      const percent = Math.max(0, Math.min(100, Number(options.percent || 0)));
      ui.progress.hidden = !options.showProgress;
      ui.progress.setAttribute('aria-valuenow', String(Math.round(percent)));
      if (ui.progressValue) ui.progressValue.style.width = `${percent}%`;
      if (ui.progressText) ui.progressText.textContent = `${Math.round(percent)} %`;
    }

    if (ui.speed) {
      ui.speed.textContent = options.speed ? `Geschwindigkeit: ${options.speed}` : '';
    }

    if (ui.remaining) {
      ui.remaining.textContent = options.remaining ? `Restzeit: ${options.remaining}` : '';
    }

    if (ui.cancel) {
      ui.cancel.hidden = !options.canCancel;
    }
  }

  function hideStatus(input) {
    const ui = getUi(input);
    if (ui.status) ui.status.hidden = true;
  }

  function getProductFormComponent(input) {
    return input.form?.closest('product-form-component') || null;
  }

  function getSubmitButtons(input) {
    const component = getProductFormComponent(input);
    if (!component) return [];
    return Array.from(component.querySelectorAll('button[type="submit"], [ref="addToCartButton"]'))
      .filter((element) => element instanceof HTMLButtonElement);
  }

  /** @type {WeakMap<HTMLButtonElement, boolean>} */
  const priorDisabledState = new WeakMap();

  function lockAddToCart(input, locked) {
    for (const button of getSubmitButtons(input)) {
      if (locked) {
        if (!priorDisabledState.has(button)) {
          priorDisabledState.set(button, button.disabled);
        }
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.dataset.lwUploadLocked = 'true';
      } else if (button.dataset.lwUploadLocked === 'true') {
        const wasDisabled = priorDisabledState.get(button) || false;
        button.disabled = wasDisabled;
        if (!wasDisabled) button.removeAttribute('aria-disabled');
        delete button.dataset.lwUploadLocked;
        priorDisabledState.delete(button);
      }
    }
  }

  function removeLargeUploadProperties(form) {
    if (!form) return;
    for (const node of form.querySelectorAll('[data-lw-large-upload-property]')) {
      node.remove();
    }
  }

  function appendProperty(form, name, value) {
    if (!form) return;
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = `properties[${name}]`;
    input.value = String(value ?? '');
    input.dataset.lwLargeUploadProperty = 'true';
    form.appendChild(input);
  }

  function attachCompletedProperties(input, result) {
    const form = input.form;
    removeLargeUploadProperties(form);

    const size = Number(result.size || result.expected_size || 0);
    const filename = String(result.filename || result.original_name || 'Druckdatei');

    appendProperty(form, 'Druckdatei', `${filename} (${formatBytes(size)})`);
    appendProperty(form, '_lw_upload_id', result.upload_id || '');
    appendProperty(form, '_lw_upload_name', filename);
    appendProperty(form, '_lw_upload_size_bytes', size);
    appendProperty(form, '_lw_upload_storage', 'allinkl');
    if (result.download_token) appendProperty(form, '_lw_download_token', result.download_token);
    if (result.sha256) appendProperty(form, '_lw_upload_sha256', result.sha256);
    if (result.detected_mime) appendProperty(form, '_lw_upload_mime', result.detected_mime);
  }

  async function retryable(action, onRetry, signal) {
    let lastError;

    for (let attempt = 0; attempt <= RETRY_DELAYS.length; attempt += 1) {
      if (signal?.aborted) throw new DOMException('Upload abgebrochen', 'AbortError');

      try {
        return await action();
      } catch (error) {
        lastError = error;
        if (signal?.aborted || error?.name === 'AbortError') throw error;
        if (attempt >= RETRY_DELAYS.length) break;
        onRetry?.(attempt + 1, RETRY_DELAYS.length);
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS[attempt]));
      }
    }

    throw lastError;
  }

  async function serverStatus(session) {
    return fetchJson(`${API_BASE}/status.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        upload_id: session.uploadId,
        upload_token: session.token,
      }),
    });
  }

  async function cancelServerUpload(session) {
    if (!session?.uploadId || !session?.token) return;
    try {
      await fetchJson(`${API_BASE}/cancel.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          upload_id: session.uploadId,
          upload_token: session.token,
        }),
      });
    } catch {
      // Abbruch bleibt fuer den Kunden erfolgreich. Verwaiste Chunks werden serverseitig bereinigt.
    }
  }

  function fileSignature(file) {
    return {
      filename: file.name,
      size: file.size,
      lastModified: file.lastModified || 0,
    };
  }

  function sessionMatchesFile(session, file) {
    return session
      && session.filename === file.name
      && Number(session.size) === file.size
      && Number(session.lastModified || 0) === Number(file.lastModified || 0);
  }

  async function initializeSession(input, file, signal) {
    const existing = loadSession(input);

    if (sessionMatchesFile(existing, file) && existing.uploadId && existing.token) {
      try {
        const status = await serverStatus(existing);
        if (status.status === 'completed') {
          return { session: existing, status, resumed: true, completed: true };
        }
        return { session: existing, status, resumed: true, completed: false };
      } catch {
        clearSession(input);
      }
    }

    const init = await fetchJson(`${API_BASE}/init.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal,
      body: JSON.stringify({
        filename: file.name,
        size: file.size,
        mime: file.type || '',
      }),
    });

    const session = {
      uploadId: init.upload_id,
      token: init.upload_token,
      chunkSize: Number(init.chunk_size),
      chunkCount: Number(init.chunk_count),
      ...fileSignature(file),
      createdAt: Date.now(),
    };

    saveSession(input, session);

    return {
      session,
      status: {
        received_chunks: 0,
        chunk_count: session.chunkCount,
        status: 'initialized',
      },
      resumed: false,
      completed: false,
    };
  }

  async function uploadChunk(session, file, chunkIndex, controller) {
    const start = chunkIndex * session.chunkSize;
    const end = Math.min(start + session.chunkSize, file.size);
    const blob = file.slice(start, end);

    const body = new FormData();
    body.set('upload_id', session.uploadId);
    body.set('upload_token', session.token);
    body.set('chunk_index', String(chunkIndex));
    body.set('chunk_count', String(session.chunkCount));
    body.set('chunk', blob, `${file.name}.part-${chunkIndex}`);

    return fetchJson(`${API_BASE}/chunk.php`, {
      method: 'POST',
      body,
      signal: controller.signal,
    });
  }

  async function completeUpload(session, controller) {
    return fetchJson(`${API_BASE}/complete.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal: controller.signal,
      body: JSON.stringify({
        upload_id: session.uploadId,
        upload_token: session.token,
      }),
    });
  }

  function showFailure(input, file, error) {
    const message = error?.message || 'Die Druckdatei konnte nicht hochgeladen werden.';
    setBoxState(input, 'error', message);
    setStatus(input, 'error', {
      title: 'Upload nicht abgeschlossen',
      detail: `${file.name} · ${message} Bitte versuchen Sie es erneut.`,
      showProgress: false,
      canCancel: false,
    });
  }

  async function startLargeUpload(input, file) {
    const controller = new AbortController();
    input._lwUploadController = controller;
    input._lwUploadCancelled = false;

    activeUploads += 1;
    input.disabled = true;
    lockAddToCart(input, true);
    setBoxState(input, 'uploading', 'Druckdatei wird noch hochgeladen.');

    const startedAt = performance.now();
    let session = null;

    try {
      setStatus(input, 'preparing', {
        title: 'Druckdaten werden vorbereitet',
        detail: `${file.name} · ${formatBytes(file.size)}`,
        showProgress: true,
        percent: 0,
        speed: 'wird berechnet',
        remaining: 'wird berechnet',
        canCancel: true,
      });

      const prepared = await initializeSession(input, file, controller.signal);
      session = prepared.session;
      input._lwUploadSession = session;

      if (prepared.completed) {
        const restored = {
          ...prepared.status,
          upload_id: session.uploadId,
          filename: prepared.status.filename || file.name,
          size: prepared.status.size || file.size,
        };
        attachCompletedProperties(input, restored);
        input.value = '';
        setBoxState(input, 'completed', 'Druckdatei wurde erfolgreich hochgeladen.');
        setStatus(input, 'completed', {
          title: 'Druckdatei erfolgreich hochgeladen',
          detail: `${file.name} · ${formatBytes(file.size)} · wird Ihrer Bestellung zugeordnet`,
          showProgress: true,
          percent: 100,
          canCancel: false,
        });
        clearSession(input);
        return;
      }

      const receivedChunks = Math.max(0, Number(prepared.status?.received_chunks || 0));
      let transferredBytes = Math.min(receivedChunks * session.chunkSize, file.size);

      for (let index = receivedChunks; index < session.chunkCount; index += 1) {
        const chunkStart = index * session.chunkSize;
        const chunkEnd = Math.min(chunkStart + session.chunkSize, file.size);
        const chunkBytes = chunkEnd - chunkStart;

        await retryable(
          () => uploadChunk(session, file, index, controller),
          (attempt, maxAttempts) => {
            setBoxState(input, 'retrying', 'Verbindung unterbrochen. Upload wird automatisch fortgesetzt.');
            setStatus(input, 'retrying', {
              title: 'Verbindung unterbrochen – Upload wird fortgesetzt',
              detail: `${file.name} · neuer Versuch ${attempt} von ${maxAttempts}`,
              showProgress: true,
              percent: (transferredBytes / file.size) * 100,
              canCancel: true,
            });
          },
          controller.signal,
        );

        transferredBytes += chunkBytes;
        const elapsedSeconds = Math.max((performance.now() - startedAt) / 1000, 0.2);
        const speed = transferredBytes / elapsedSeconds;
        const remainingSeconds = speed > 0 ? (file.size - transferredBytes) / speed : NaN;
        const percent = (transferredBytes / file.size) * 100;

        setBoxState(input, 'uploading', 'Druckdatei wird noch hochgeladen.');
        setStatus(input, 'uploading', {
          title: 'Druckdaten werden hochgeladen',
          detail: `${formatBytes(transferredBytes)} von ${formatBytes(file.size)} übertragen · Bitte diese Seite nicht schließen.`,
          showProgress: true,
          percent,
          speed: formatSpeed(speed),
          remaining: formatRemaining(remainingSeconds),
          canCancel: true,
        });
      }

      setStatus(input, 'finalizing', {
        title: 'Upload wird abgeschlossen',
        detail: 'Die Datei wird geprüft und sicher gespeichert. Bitte diese Seite noch geöffnet lassen.',
        showProgress: true,
        percent: 100,
        canCancel: false,
      });

      const result = await completeUpload(session, controller);
      attachCompletedProperties(input, result);

      // Verhindert, dass Shopify die grosse Datei zusaetzlich selbst hochlaedt.
      input.value = '';
      input.disabled = false;

      setBoxState(input, 'completed', 'Druckdatei wurde erfolgreich hochgeladen.');
      setStatus(input, 'completed', {
        title: 'Druckdatei erfolgreich hochgeladen',
        detail: `${result.filename || file.name} · ${formatBytes(Number(result.size || file.size))} · wird Ihrer Bestellung zugeordnet`,
        showProgress: true,
        percent: 100,
        canCancel: false,
      });

      clearSession(input);
    } catch (error) {
      if (error?.name === 'AbortError' || input._lwUploadCancelled) {
        setBoxState(input, 'idle', '');
        setStatus(input, 'idle', {
          title: 'Upload abgebrochen',
          detail: 'Sie können eine andere Datei auswählen.',
          showProgress: false,
          canCancel: false,
        });
      } else {
        showFailure(input, file, error);
      }
    } finally {
      activeUploads = Math.max(0, activeUploads - 1);
      lockAddToCart(input, false);
      input.disabled = false;
      input._lwUploadController = null;
    }
  }

  async function handleFileSelection(input) {
    const file = input.files?.[0];
    removeLargeUploadProperties(input.form);
    setBoxState(input, 'idle', '');

    if (!file) {
      hideStatus(input);
      return;
    }

    const extension = getExtension(file.name);
    if (!ALL_ALLOWED_EXTENSIONS.has(extension)) {
      const message = 'Dieser Dateityp ist nicht freigegeben. Erlaubt sind PDF, JPG, PNG, TIFF, PSD, HEIC/HEIF, AI, EPS und SVG.';
      input.value = '';
      setBoxState(input, 'error', message);
      setStatus(input, 'error', {
        title: 'Dateityp nicht unterstützt',
        detail: message,
        showProgress: false,
        canCancel: false,
      });
      return;
    }

    if (file.size > ALLINKL_MAX_LIMIT) {
      const message = 'Die Datei ist größer als 350 MB. Bitte stellen Sie die Druckdaten über einen Downloadlink bereit oder kontaktieren Sie uns.';
      input.value = '';
      setBoxState(input, 'error', message);
      setStatus(input, 'error', {
        title: 'Datei ist größer als 350 MB',
        detail: message,
        showProgress: false,
        canCancel: false,
      });
      return;
    }

    const canUseShopify = file.size <= SHOPIFY_DIRECT_LIMIT && SHOPIFY_DIRECT_EXTENSIONS.has(extension);

    if (canUseShopify) {
      clearSession(input);
      setBoxState(input, 'native', '');
      setStatus(input, 'native', {
        title: 'Druckdatei ausgewählt',
        detail: `${file.name} · ${formatBytes(file.size)} · wird beim Hinzufügen zum Warenkorb sicher über Shopify übertragen.`,
        showProgress: false,
        canCancel: false,
      });
      return;
    }

    await startLargeUpload(input, file);
  }

  async function restorePendingState(input) {
    const session = loadSession(input);
    if (!session?.uploadId || !session?.token) return;

    try {
      const status = await serverStatus(session);

      if (status.status === 'completed') {
        attachCompletedProperties(input, {
          ...status,
          upload_id: session.uploadId,
          filename: status.filename || session.filename,
          size: status.size || session.size,
        });
        setBoxState(input, 'completed', 'Druckdatei wurde erfolgreich hochgeladen.');
        setStatus(input, 'completed', {
          title: 'Druckdatei erfolgreich hochgeladen',
          detail: `${status.filename || session.filename} · ${formatBytes(Number(status.size || session.size))} · wird Ihrer Bestellung zugeordnet`,
          showProgress: true,
          percent: 100,
          canCancel: false,
        });
        clearSession(input);
        return;
      }

      const percent = Number(status.progress_percent || 0);
      setBoxState(input, 'paused', 'Ein unvollständiger Upload wurde gefunden. Bitte wählen Sie dieselbe Datei erneut aus, um fortzusetzen.');
      setStatus(input, 'paused', {
        title: 'Unvollständiger Upload gefunden',
        detail: `${session.filename} · ${formatBytes(Number(session.size || 0))} · wählen Sie dieselbe Datei erneut aus, um bei ${Math.round(percent)} % fortzusetzen.`,
        showProgress: true,
        percent,
        canCancel: true,
      });
      input._lwUploadSession = session;
    } catch {
      clearSession(input);
    }
  }

  function initInput(input) {
    if (initializedInputs.has(input)) return;
    initializedInputs.add(input);

    input.addEventListener('change', () => {
      handleFileSelection(input);
    });

    if (input.dataset.lwUploadRequired === 'true' && input.form) {
      input.form.addEventListener('submit', (event) => {
        const box = input.closest('[data-lw-personalization]');
        const state = box?.dataset.lwLargeUploadState || '';
        const hasNativeFile = state === 'native' && Boolean(input.files?.[0]);
        const hasCompletedUpload = Boolean(
          input.form.querySelector('input[name="properties[_lw_upload_id]"]')
        );

        if (hasNativeFile || hasCompletedUpload) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const message = 'Bitte laden Sie zuerst Ihre Druckdatei hoch.';
        setBoxState(input, 'error', message);
        setStatus(input, 'error', {
          title: 'Druckdatei erforderlich',
          detail: message,
          showProgress: false,
          canCancel: false,
        });

        input.focus({ preventScroll: true });
        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, true);
    }

    const ui = getUi(input);
    ui.cancel?.addEventListener('click', async () => {
      input._lwUploadCancelled = true;
      input._lwUploadController?.abort();
      const session = input._lwUploadSession || loadSession(input);
      await cancelServerUpload(session);
      clearSession(input);
      removeLargeUploadProperties(input.form);
      input.value = '';
      input.disabled = false;
      lockAddToCart(input, false);
      setBoxState(input, 'idle', '');
      setStatus(input, 'idle', {
        title: 'Upload abgebrochen',
        detail: 'Sie können eine andere Datei auswählen.',
        showProgress: false,
        canCancel: false,
      });
    });

    restorePendingState(input);
  }

  function initAll(root = document) {
    for (const input of root.querySelectorAll?.('[data-lw-upload-input]') || []) {
      if (input instanceof HTMLInputElement) initInput(input);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target instanceof Element ? event.target : document);
  });
})();
